export type Palette = {
  id: string;
  fieldA: number;
  fieldB: number;
  player: number;
  receiver: number;
  accent: number;
  text: number;
  /** Legacy / contact motif ink on field A. */
  subtle: number;
  /** Contact maze ink on field B. */
  subtleB: number;
  cursor: number;
  splitRatio: number;
  markLayout: 0 | 1 | 2;
  weight?: number;
};

/**
 * Poster fields stay bold; player/receiver/accent avoid harsh matching reds.
 * Accent is FIELD-cue only and must read on fieldB.
 */
export const PALETTES: Palette[] = [
  {
    id: 'pink-lemon-cobalt',
    fieldA: 0xff2d95,
    fieldB: 0xffe94a,
    player: 0x1f4fd8,
    receiver: 0x1a1a1a,
    accent: 0x1f4fd8,
    text: 0x1a1a1a,
    subtle: 0xe01878,
    subtleB: 0xd8c830,
    cursor: 0x1a1a1a,
    splitRatio: 0.58,
    markLayout: 0,
    weight: 3,
  },
  {
    id: 'hotpink-cream-cobalt',
    fieldA: 0xff1a8c,
    fieldB: 0xf3ebe0,
    player: 0x2048d4,
    receiver: 0x1a1a1a,
    accent: 0x2048d4,
    text: 0x1a1a1a,
    subtle: 0xe01078,
    subtleB: 0xddd5c8,
    cursor: 0x1a1a1a,
    splitRatio: 0.55,
    markLayout: 1,
    weight: 3,
  },
  {
    id: 'cyan-cream-cobalt',
    fieldA: 0x00c4e4,
    fieldB: 0xf4efe4,
    player: 0x1f3fd0,
    receiver: 0x1a1a1a,
    accent: 0xff2d95,
    text: 0x1a1a1a,
    subtle: 0x00accc,
    subtleB: 0xddd5c8,
    cursor: 0x1a1a1a,
    splitRatio: 0.58,
    markLayout: 0,
    weight: 3,
  },
  {
    id: 'cobalt-hotpink-cream',
    fieldA: 0x1848d8,
    fieldB: 0xff3eb5,
    player: 0xf4efe4,
    receiver: 0xf4efe4,
    accent: 0xffef3a,
    text: 0xf4efe4,
    subtle: 0x143cb8,
    subtleB: 0xe0289c,
    cursor: 0xf4efe4,
    splitRatio: 0.61,
    markLayout: 1,
    weight: 3,
  },
  {
    id: 'violet-lemon-cream',
    fieldA: 0x5b1fa8,
    fieldB: 0xdfff2e,
    player: 0xf4efe4,
    receiver: 0x1a1028,
    accent: 0xff2d95,
    text: 0x1a1028,
    subtle: 0x4a188c,
    subtleB: 0xc8e620,
    cursor: 0x1a1028,
    splitRatio: 0.55,
    markLayout: 2,
    weight: 2,
  },
  {
    id: 'magenta-mint-navy',
    fieldA: 0xe6007a,
    fieldB: 0x2affd0,
    player: 0x0a1a5c,
    receiver: 0x0a1a5c,
    accent: 0x0a1a5c,
    text: 0x0a1a5c,
    subtle: 0xc80068,
    subtleB: 0x18e0b4,
    cursor: 0x0a1a5c,
    splitRatio: 0.58,
    markLayout: 0,
    weight: 2,
  },
  {
    id: 'lemon-violet-cream',
    fieldA: 0xffef28,
    fieldB: 0x5a1fb8,
    player: 0xf4efe4,
    receiver: 0xffef28,
    accent: 0xffef28,
    text: 0xffefc8,
    subtle: 0xe6d61a,
    subtleB: 0x4a189c,
    cursor: 0xffef28,
    splitRatio: 0.61,
    markLayout: 1,
    weight: 2,
  },
  {
    id: 'ink-cream-magenta',
    fieldA: 0x121820,
    fieldB: 0xf3ebe0,
    player: 0xff2d95,
    receiver: 0x121820,
    accent: 0x00c4e4,
    text: 0x121820,
    subtle: 0x1c2430,
    subtleB: 0xddd5c8,
    cursor: 0x121820,
    splitRatio: 0.58,
    markLayout: 0,
    weight: 2,
  },
  {
    id: 'vermilion-cyan-cream',
    fieldA: 0xff3b1f,
    fieldB: 0x00d8f0,
    player: 0xf4efe4,
    receiver: 0xf4efe4,
    accent: 0x121820,
    text: 0x121820,
    subtle: 0xe03018,
    subtleB: 0x00c0d4,
    cursor: 0x121820,
    splitRatio: 0.61,
    markLayout: 1,
    weight: 1,
  },
  {
    id: 'coral-navy-cream',
    fieldA: 0xff5a4a,
    fieldB: 0x1a2a5c,
    player: 0xf4efe4,
    receiver: 0xf4efe4,
    accent: 0xffef3a,
    text: 0xf4efe4,
    subtle: 0xe0483a,
    subtleB: 0x142048,
    cursor: 0xf4efe4,
    splitRatio: 0.55,
    markLayout: 2,
    weight: 1,
  },
];

/** Fixed neutral split for the align landing — no random poster palettes. */
export const DEFAULT_PALETTE: Palette = {
  id: 'steele-neutral',
  fieldA: 0x121820,
  fieldB: 0xf3ebe0,
  player: 0xff2d95,
  receiver: 0x121820,
  accent: 0xff2d95,
  text: 0x121820,
  subtle: 0x1c2430,
  subtleB: 0xddd5c8,
  cursor: 0x121820,
  splitRatio: 0.58,
  markLayout: 1,
};

const PREV_KEY = 'steele.prevPaletteId';

function weightedPick(list: Palette[], seed: number): Palette {
  const total = list.reduce((sum, p) => sum + (p.weight ?? 1), 0);
  let roll = seed * total;
  for (const p of list) {
    roll -= p.weight ?? 1;
    if (roll <= 0) return p;
  }
  return list[list.length - 1];
}

export function pickPalette(seed = Math.random()): Palette {
  let previous: string | null = null;
  try {
    previous = sessionStorage.getItem(PREV_KEY);
  } catch {
    // ignore
  }

  const pool = previous ? PALETTES.filter((p) => p.id !== previous) : PALETTES.slice();
  const list = pool.length > 0 ? pool : PALETTES;
  const palette = weightedPick(list, seed);

  try {
    sessionStorage.setItem(PREV_KEY, palette.id);
  } catch {
    // ignore
  }

  return palette;
}

export function pickNextPalette(currentId: string): Palette {
  const pool = PALETTES.filter((p) => p.id !== currentId);
  const list = pool.length > 0 ? pool : PALETTES;
  const palette = weightedPick(list, Math.random());
  try {
    sessionStorage.setItem(PREV_KEY, palette.id);
  } catch {
    // ignore
  }
  return palette;
}

export function applyPaletteToDocument(palette: Palette): void {
  const root = document.documentElement;
  root.style.setProperty('--field-a', hexCss(palette.fieldA));
  root.style.setProperty('--field-b', hexCss(palette.fieldB));
  root.style.setProperty('--player', hexCss(palette.player));
  root.style.setProperty('--receiver', hexCss(palette.receiver));
  root.style.setProperty('--accent', hexCss(palette.accent));
  root.style.setProperty('--text', hexCss(palette.text));
  root.style.setProperty('--subtle', hexCss(palette.subtle));
  root.style.setProperty('--cursor', hexCss(palette.cursor));
  root.style.setProperty('--focus', hexCss(palette.accent));
  root.style.setProperty('--motif', hexCss(palette.subtleB));
}

export function hexCss(n: number): string {
  return `#${n.toString(16).padStart(6, '0')}`;
}
